import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { WindowRefService } from '../window.service';
import { HelperService } from '../public/helper.service';
import { WhitelabelConfig } from '../model/whitelabel.config';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { FaviconService } from '../favicon.service';
import {
  StoreIntegrationsPaymentProvidersTypes,
  StoreIntegrationsPosTypes,
  StoreIntegrationType,
} from '../stores/stores';
import { LoggedInUser } from '../auth/auth';

@Injectable({
  providedIn: 'root',
})
export class WhitelabelService {
  private initPromise: Promise<void>;

  private currentWhiteLabelConfig: WhitelabelConfig;

  constructor(
    private windowRefSer: WindowRefService,
    private helper: HelperService,
    private http: HttpClient,
    private faviconService: FaviconService,
  ) {}

  waitForInit(): Promise<void> {
    return this.initPromise;
  }

  getWhiteLabelCode() {
    return this.currentWhiteLabelConfig.code;
  }

  getWhitelabelLogo() {
    return this.currentWhiteLabelConfig.logo;
  }

  getWhitelabelCommericalUrl() {
    return this.currentWhiteLabelConfig.commercialURL;
  }

  getWhitelabelPoweredBy() {
    return this.currentWhiteLabelConfig.poweredBy;
  }

  getWhiteLabelName() {
    return this.currentWhiteLabelConfig.name;
  }

  /**
   * True when the current admin session is running on the XO white-label tenant.
   *
   * <p>Introduced by #9547 as the canonical check for any XO-only admin UI behavior. Callers
   * should reuse this helper rather than open-coding the {@code getWhiteLabelName() === 'xo'}
   * comparison so the predicate (case-insensitive, null-safe) lives in one place.
   */
  isXoTenant(): boolean {
    return (this.getWhiteLabelName() || '').toLowerCase() === 'xo';
  }

  getWhiteLabelPrimaryBrandColour() {
    return this.currentWhiteLabelConfig.primaryBrandColour;
  }

  getWhiteLabelSecondaryBrandColour() {
    return this.currentWhiteLabelConfig.secondaryBrandColour;
  }

  getWhiteLabelShowHelpCenterIcon() {
    return this.currentWhiteLabelConfig.showHelpCenterIcon;
  }

  getWhiteLabelCookieLink() {
    return this.currentWhiteLabelConfig.cookiesLink;
  }

  getWhiteLabelTermsLink() {
    return this.currentWhiteLabelConfig.termsLink + '?hideheaderfooter=true';
  }

  getWhiteLabelPrivacyLink() {
    return this.currentWhiteLabelConfig.privacyLink + '?hideheaderfooter=true';
  }

  getWhiteLabelRefundsLink() {
    return this.currentWhiteLabelConfig.refundLink + '?hideheaderfooter=true';
  }

  isWhiteLabelDomainBySubDomain(adminSubDomain: string): boolean {
    // applicable when tenant has custom domain
    if (adminSubDomain.includes('.')) {
      return (
        this.extractHostname(adminSubDomain) ===
        this.extractHostname(`admin.${this.currentWhiteLabelConfig.domainUri}`)
      );
    }
    // applicable for gonna order domain
    return this.currentWhiteLabelConfig.adminSubdomain === adminSubDomain;
  }

  isWhiteLabelDomainByHostName(hostname: string): boolean {
    return (
      hostname ===
        `${this.currentWhiteLabelConfig?.adminSubdomain}.${this.currentWhiteLabelConfig?.domainUri}` ||
      hostname === `admin.${this.currentWhiteLabelConfig?.domainUri}`
    );
  }

  getSubdomain() {
    const domain = this.windowRefSer.nativeWindowLocation.hostname;

    if (domain.indexOf(environment.envDomain) < 0) {
      return domain;
    }

    if (
      domain.indexOf('.') < 0 ||
      domain.split('.')[0] === 'example' ||
      domain.split('.')[0] === 'lvh' ||
      domain.split('.')[0] === 'www'
    ) {
      return '';
    }
    return domain.split('.')[0];
  }

  getHost() {
    if (this.helper.isMobileApp()) {
      return environment.envDomain;
    }
    return this.currentWhiteLabelConfig.domainUri;
  }

  loadWhiteLabelData(): Promise<void> {
    let headers = new HttpHeaders();

    if (this.helper.isGoAdminApp() || this.helper.isGoKioskApp()) {
      headers = headers.set('x-referer', environment.tenantDomain);
    }
    this.initPromise = this.http
      .get<WhitelabelConfig>('/api/v1/refdata/tenants/current-tenant', {
        headers: headers,
      })
      .toPromise()
      .then((data) => {
        this.currentWhiteLabelConfig = data;
        this.faviconService.changeFavicon(data.code);
      });

    return this.initPromise;
  }

  getWhiteLabelSupportWhatsAppNumber() {
    return this.currentWhiteLabelConfig.supportWhatsAppNumber;
  }

  getWhiteLabelSupportTelegramUsername() {
    return this.currentWhiteLabelConfig.supportTelegramUsername;
  }

  getWhiteLabelDomainUri() {
    return this.currentWhiteLabelConfig.domainUri;
  }

  getSocialLoginEnabled() {
    return (
      (this.currentWhiteLabelConfig.socialLoginEnabled || '').toLowerCase() ===
      'yes'
    );
  }

  getShowBrandFigure() {
    return this.currentWhiteLabelConfig.code === 'gonnaorder';
  }

  getEventManagementEnabled() {
    return (
      (
        this.currentWhiteLabelConfig.eventManagementEnabled || ''
      ).toLowerCase() === 'yes'
    );
  }

  getStripeSubscriptionUrl() {
    return this.currentWhiteLabelConfig.stripeSubscriptionUrl;
  }
  getAdminHostURL() {
    return this.currentWhiteLabelConfig.adminHostURL;
  }

  getGoogleCliendId() {
    return this.currentWhiteLabelConfig?.googleClientId;
  }

  getFacebookAppId() {
    return this.currentWhiteLabelConfig?.facebookAppId;
  }

  getAppleAppId() {
    return this.currentWhiteLabelConfig?.appleAppId;
  }

  getCustomSocialSigninEnable() {
    return this.currentWhiteLabelConfig?.customSocialSignin;
  }

  getEposNowInstallLink() {
    return this.currentWhiteLabelConfig?.eposNowInstallLink;
  }

  getIntegrationsPaymentProvidersActive() {
    return this.currentWhiteLabelConfig?.integrationsPaymentProvidersActive;
  }

  getIntegrationsPosActive() {
    return this.currentWhiteLabelConfig?.integrationsPosActive;
  }

  isTenantIntegrationActive(
    integrationType: StoreIntegrationType,
    integrationName:
      | StoreIntegrationsPosTypes
      | StoreIntegrationsPaymentProvidersTypes,
    loggedInUser: LoggedInUser = null,
  ): boolean {
    if (loggedInUser?.superAdmin || loggedInUser?.platformAdmin) {
      return true;
    }

    switch (integrationType) {
      case StoreIntegrationType.PAYMENT_PROVIDERS:
        return this.isTenantIntegrationPaymentProviderActive(
          integrationName as StoreIntegrationsPaymentProvidersTypes,
        );
      case StoreIntegrationType.POS:
        return this.isTenantIntegrationPosActive(
          integrationName as StoreIntegrationsPosTypes,
        );
    }
  }

  private isTenantIntegrationPosActive(
    integrationName: StoreIntegrationsPosTypes,
  ): boolean {
    return (
      this.getIntegrationsPosActive().includes('*') ||
      this.getIntegrationsPosActive().includes(integrationName)
    );
  }

  private isTenantIntegrationPaymentProviderActive(
    paymentProvider: StoreIntegrationsPaymentProvidersTypes,
  ): boolean {
    return (
      this.getIntegrationsPaymentProvidersActive().includes('*') ||
      this.getIntegrationsPaymentProvidersActive().includes(paymentProvider)
    );
  }

  extractHostname(url: string) {
    try {
      // Add protocol to make it a valid URL, allowing the URL constructor to parse it
      return new URL(`https://${url}`).hostname;
    } catch {
      return url; // If it's not a valid URL, return it as is (for cases without a dot)
    }
  }

  isTelesignEnabled() {
    return this.currentWhiteLabelConfig?.telesignEnabled;
  }

  isTenantOrdersAiEnabled() {
    return this.currentWhiteLabelConfig?.tenantOrdersAiEnabled;
  }

  isRecaptchaEnabled() {
    return this.currentWhiteLabelConfig?.recaptchaEnabled ?? true;
  }
}
